<script setup>
import moment from 'moment-timezone';
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const allInboxes = useMapGetter('inboxes/getInboxes');

const DEFAULT_TIMEZONE = 'Asia/Saigon';

const isActive = computed(() => props.selectedCampaign?.status === 'active');

const initialState = {
  name: '',
  description: '',
  inboxIds: [],
  hashtags: [],
  timezone: DEFAULT_TIMEZONE,
  startsAt: '',
  endsAt: '',
  publicContent: '',
  privateContent: '',
  referenceContent: '',
};

const state = reactive({ ...initialState });

const isEndDateAfterStartDate = helpers.withMessage(
  () => t('SMART_CAMPAIGNS.FORM.ENDS_AT.ERROR_AFTER_START'),
  value => {
    if (!state.startsAt || !value) return true;
    return (
      moment.tz(value, 'YYYY-MM-DDTHH:mm', state.timezone).toDate() >
      moment.tz(state.startsAt, 'YYYY-MM-DDTHH:mm', state.timezone).toDate()
    );
  }
);

const rules = computed(() => ({
  name: { required },
  description: { required },
  inboxIds: { required },
  publicContent: { required },
  startsAt: { required },
  endsAt: { required, isEndDateAfterStartDate },
}));

const v$ = useVuelidate(rules, state);

const inboxOptions = computed(() =>
  (allInboxes.value ?? []).map(inbox => ({
    id: inbox.id,
    name: inbox.name,
  }))
);

const timeZoneList = computed(() => [...timeZoneOptions()]);

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  name: v$.value.name?.$error ? t('SMART_CAMPAIGNS.FORM.NAME.ERROR') : '',
  description: v$.value.description?.$error
    ? t('SMART_CAMPAIGNS.FORM.DESCRIPTION.ERROR')
    : '',
  inboxIds: v$.value.inboxIds?.$error
    ? t('SMART_CAMPAIGNS.FORM.INBOXES.ERROR')
    : '',
  publicContent: v$.value.publicContent?.$error
    ? t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.ERROR')
    : '',
  privateContent: v$.value.privateContent?.$error
    ? t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.ERROR')
    : '',
  startsAt: v$.value.startsAt?.$error
    ? v$.value.startsAt.$errors[0]?.$message ||
      t('SMART_CAMPAIGNS.FORM.STARTS_AT.ERROR')
    : '',
  endsAt: v$.value.endsAt?.$error
    ? v$.value.endsAt.$errors[0]?.$message ||
      t('SMART_CAMPAIGNS.FORM.ENDS_AT.ERROR')
    : '',
}));

const expandedField = ref(null);

const expandedConfigs = {
  publicContent: {
    label: t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.LABEL'),
    placeholder: t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.PLACEHOLDER'),
  },
  privateContent: {
    label: t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.LABEL'),
    placeholder: t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.PLACEHOLDER'),
  },
  referenceContent: {
    label: t('SMART_CAMPAIGNS.FORM.REFERENCE_CONTENT.LABEL'),
    placeholder: t('SMART_CAMPAIGNS.FORM.REFERENCE_CONTENT.PLACEHOLDER'),
  },
};

const resetState = () =>
  Object.assign(state, { ...initialState, inboxIds: [], hashtags: [] });

const toDatetimeLocal = (dateString, tz) => {
  if (!dateString) return '';
  return moment(dateString)
    .tz(tz || DEFAULT_TIMEZONE)
    .format('YYYY-MM-DDTHH:mm');
};

const populateFromCampaign = campaign => {
  if (!campaign) return;
  Object.assign(state, {
    name: campaign.name ?? '',
    description: campaign.description ?? '',
    inboxIds: (campaign.campaign_config?.inbox_ids ?? []).map(id => {
      const inbox = (allInboxes.value ?? []).find(i => i.id === id);
      return inbox ? { id: inbox.id, name: inbox.name } : { id, name: '' };
    }),
    hashtags: campaign.campaign_config?.hashtags ?? [],
    timezone: campaign.campaign_config?.timezone || DEFAULT_TIMEZONE,
    startsAt: toDatetimeLocal(
      campaign.starts_at,
      campaign.campaign_config?.timezone || DEFAULT_TIMEZONE
    ),
    endsAt: toDatetimeLocal(
      campaign.ends_at,
      campaign.campaign_config?.timezone || DEFAULT_TIMEZONE
    ),
    publicContent: campaign.public_content ?? '',
    privateContent: campaign.private_content ?? '',
    referenceContent: campaign.reference_content ?? '',
  });
};

watch(
  () => props.selectedCampaign,
  campaign => {
    if (props.mode === 'edit' && campaign) populateFromCampaign(campaign);
  },
  { immediate: true }
);

const prepareCampaignDetails = () => ({
  name: state.name,
  description: state.description,
  public_content: state.publicContent,
  private_content: state.privateContent,
  reference_content: state.referenceContent,
  starts_at: state.startsAt
    ? moment
        .tz(state.startsAt, 'YYYY-MM-DDTHH:mm', state.timezone)
        .toISOString()
    : null,
  ends_at: state.endsAt
    ? moment.tz(state.endsAt, 'YYYY-MM-DDTHH:mm', state.timezone).toISOString()
    : null,
  campaign_config: {
    timezone: state.timezone,
    hashtags: state.hashtags,
    inbox_ids: state.inboxIds.map(i => i.id),
  },
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return null;
  return prepareCampaignDetails();
};

defineExpose({ isSubmitDisabled, handleSubmit, resetState });
</script>

<template>
  <div class="grid grid-cols-2 gap-6">
    <div class="space-y-4 px-1">
      <Input
        v-model="state.name"
        :label="t('SMART_CAMPAIGNS.FORM.NAME.LABEL')"
        :placeholder="t('SMART_CAMPAIGNS.FORM.NAME.PLACEHOLDER')"
        :message="formErrors.name"
        :message-type="formErrors.name ? 'error' : 'info'"
        autofocus
        @blur="v$.name.$touch()"
      />

      <TextArea
        v-model="state.description"
        class="!h-auto"
        :label="t('SMART_CAMPAIGNS.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('SMART_CAMPAIGNS.FORM.DESCRIPTION.PLACEHOLDER')"
        :max-length="500"
        show-character-count
        auto-height
        min-height="6rem"
        max-height="6rem"
        :message="formErrors.description"
        :message-type="formErrors.description ? 'error' : 'info'"
        @blur="v$.description.$touch()"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('SMART_CAMPAIGNS.FORM.INBOXES.LABEL') }}
        </label>
        <MultiSelect
          v-model="state.inboxIds"
          :options="inboxOptions"
          :max-chips="2"
          @blur="v$.inboxIds.$touch()"
        />
        <p v-if="formErrors.inboxIds" class="text-xs text-r-red-9 mt-0.5">
          {{ formErrors.inboxIds }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('SMART_CAMPAIGNS.FORM.HASHTAGS.LABEL') }}
        </label>
        <div
          class="flex flex-wrap gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-blue-8 min-h-[2.75rem] cursor-text"
        >
          <TagInput
            v-model="state.hashtags"
            :placeholder="t('SMART_CAMPAIGNS.FORM.HASHTAGS.PLACEHOLDER')"
            allow-create
            :auto-open-dropdown="false"
          />
        </div>
        <p class="text-xs text-n-slate-10 mt-0.5">
          {{ t('SMART_CAMPAIGNS.FORM.HASHTAGS.HINT') }}
        </p>
      </div>

      <div
        class="flex flex-col gap-3 rounded-xl border border-n-weak p-4 bg-n-alpha-black2"
      >
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('SMART_CAMPAIGNS.FORM.TIMEZONE.LABEL') }}
          </label>
          <ComboBox
            v-model="state.timezone"
            :options="timeZoneList"
            :placeholder="t('SMART_CAMPAIGNS.FORM.TIMEZONE.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{
              isActive
                ? t('SMART_CAMPAIGNS.FORM.TIMEZONE.HINT_RUNNING')
                : t('SMART_CAMPAIGNS.FORM.TIMEZONE.HINT')
            }}
          </p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="state.startsAt"
            :label="t('SMART_CAMPAIGNS.FORM.STARTS_AT.LABEL')"
            type="datetime-local"
            :placeholder="t('SMART_CAMPAIGNS.FORM.STARTS_AT.PLACEHOLDER')"
            :message="formErrors.startsAt"
            :message-type="formErrors.startsAt ? 'error' : 'info'"
            @blur="v$.startsAt?.$touch()"
          />
          <Input
            v-model="state.endsAt"
            :label="t('SMART_CAMPAIGNS.FORM.ENDS_AT.LABEL')"
            type="datetime-local"
            :min="state.startsAt"
            :placeholder="t('SMART_CAMPAIGNS.FORM.ENDS_AT.PLACEHOLDER')"
            :message="formErrors.endsAt"
            :message-type="formErrors.endsAt ? 'error' : 'info'"
            @blur="v$.endsAt?.$touch()"
          />
        </div>
      </div>
    </div>

    <div class="space-y-4 pr-1">
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between mb-0.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.LABEL') }}
          </label>
          <Button
            type="button"
            icon="i-lucide-expand"
            xs
            ghost
            slate
            @click="expandedField = 'publicContent'"
          />
        </div>
        <TextArea
          v-model="state.publicContent"
          class="!h-auto"
          :placeholder="t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.PLACEHOLDER')"
          :max-length="3000"
          show-character-count
          auto-height
          min-height="7rem"
          max-height="7rem"
          :message="
            formErrors.publicContent ||
            t('SMART_CAMPAIGNS.FORM.PUBLIC_CONTENT.HINT')
          "
          :message-type="formErrors.publicContent ? 'error' : 'info'"
          @blur="v$.publicContent.$touch()"
        />
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between mb-0.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.LABEL') }}
          </label>
          <Button
            type="button"
            icon="i-lucide-expand"
            xs
            ghost
            slate
            @click="expandedField = 'privateContent'"
          />
        </div>
        <TextArea
          v-model="state.privateContent"
          class="!h-auto"
          :placeholder="t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.PLACEHOLDER')"
          :max-length="3000"
          show-character-count
          auto-height
          min-height="7rem"
          max-height="7rem"
          :message="
            formErrors.privateContent ||
            t('SMART_CAMPAIGNS.FORM.PRIVATE_CONTENT.HINT')
          "
          :message-type="formErrors.privateContent ? 'error' : 'info'"
          @blur="v$.privateContent.$touch()"
        />
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between mb-0.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('SMART_CAMPAIGNS.FORM.REFERENCE_CONTENT.LABEL') }}
          </label>
          <Button
            type="button"
            icon="i-lucide-expand"
            xs
            ghost
            slate
            @click="expandedField = 'referenceContent'"
          />
        </div>
        <TextArea
          v-model="state.referenceContent"
          class="!h-auto"
          :placeholder="t('SMART_CAMPAIGNS.FORM.REFERENCE_CONTENT.PLACEHOLDER')"
          :max-length="3000"
          show-character-count
          auto-height
          min-height="7rem"
          max-height="7rem"
          :message="t('SMART_CAMPAIGNS.FORM.REFERENCE_CONTENT.HINT')"
          message-type="info"
        />
      </div>
    </div>
  </div>

  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-150 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="expandedField"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
        @click.self="expandedField = null"
      >
        <div
          class="bg-n-solid-1 rounded-xl shadow-2xl w-3/4 max-w-4xl flex flex-col gap-4 p-6"
        >
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ expandedConfigs[expandedField]?.label }}
            </h3>
            <Button
              type="button"
              icon="i-lucide-x"
              xs
              ghost
              slate
              @click="expandedField = null"
            />
          </div>
          <TextArea
            v-model="state[expandedField]"
            :placeholder="expandedConfigs[expandedField]?.placeholder"
            :max-length="3000"
            show-character-count
            auto-height
            min-height="32rem"
            max-height="32rem"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
